import { useState } from "react";

import { loadLocale } from "@smart/itops-locale-dom";
import {
  Button,
  ContextMenu,
  Icon,
  Link,
  Modal,
  SearchInput,
  Select,
  Table,
} from "@smart/itops-sb-design-system-dom";
import { useLazyLoadTeams } from "@smart/manage-gql-client-dom";
import { useManageFormsContext } from "@smart/manage-hooks-dom";

import { ActAs } from "./act-as";
import { useFormColumns } from "./columns";
import { CreatingFormModal } from "./creating";
import { useFormData } from "./data";
import { DeletingFormModal } from "./deleting";
import { DuplicatingFormModal } from "./duplicating";
import { isReviewView } from "./review-view";
import { SetupModal } from "./setup";
import { useReviewFormsNavigation } from "../../settings/navigate";
import { Banner, VoiceGuideBanner } from "../../shared";
import { ActingAsLabel } from "../shared/acting-as-label";
import {
  GqlDeletedForm,
  GqlForm,
  GqlSetting,
  GqlSubmissionLinkHash,
} from "../types";

const styles = {
  heading:
    "flex px-10 py-2 gap-8 items-center justify-between bg-white border-b border-neutral-100",
  page: "m-auto p-10 max-w-screen-maximum",
  container: "flex flex-col w-full bg-white rounded",
  toolbarWrapper: "flex text-ui max-w-screen-maximum",
  toolbar: "flex justify-between flex-1 max-w-screen-maximum mx-auto p-6",
  toolbarFilters: "flex items-center gap-3 flex-wrap",
  toolbarActions: "flex items-start min-w-[140px] gap-4",
};

export type ListFormsProps = {
  loading?: boolean;
  forms?: GqlForm[];
  deletedForms?: GqlDeletedForm[];
  setting?: GqlSetting;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
  highlightReminderMessageFilter?: boolean;
};

export const ListForms = ({
  loading,
  forms,
  deletedForms,
  setting,
  submissionLinkHashes,
  highlightReminderMessageFilter,
}: ListFormsProps) => {
  const { terms } = loadLocale();
  const [creatingForm, setCreatingForm] = useState<true>();
  const { backToSettings, canGoBack } = useReviewFormsNavigation();
  const inReviewView = isReviewView();
  const [highlightReminderMessage, setHighlightReminderMessage] = useState(
    !!highlightReminderMessageFilter,
  );
  const clearHighlight = () => setHighlightReminderMessage(false);
  const { data, filterOptions, filters } = useFormData({
    forms,
    deletedForms,
    submissionLinkHashes,
    highlightReminderMessage,
  });
  const { canActAsFirm } = useManageFormsContext();
  const [loadTeam, { data: loadedTeam, error, loading: loadingTeam }] =
    useLazyLoadTeams();
  const [errorLoadingTeam, setErrorLoadingTeam] = useState(error);
  const {
    columns,
    globalFilter,
    select,
    sort,
    deletingForm,
    setDeletingForm,
    duplicatingForm,
    setDuplicatingForm,
    onRowClick,
    printFormContent,
  } = useFormColumns();

  const getEmptyMessage = () => {
    const filterValue = filters[0].value;
    if (filterValue === "deleted") {
      return <p>No deleted forms found.</p>;
    }
    if (filterValue === "webEnquiry") {
      return <p>{`No Web ${terms.enquiry.title} forms found.`}</p>;
    }
    return (
      <>
        <p>No forms {forms?.length ? "found" : "available"}.</p>
        <p>To get started, create a form above.</p>
      </>
    );
  };

  return (
    <div className="w-screen min-h-screen bg-neutral-50">
      <div className={styles.heading}>
        <h3 className="my-5 mx-0 font-semibold text-h3 font-poppins">
          Intake Forms
        </h3>
        <div className="flex items-center gap-8">
          <ActingAsLabel />
          {inReviewView && canGoBack && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              text="Back to Intake Settings"
              leftIcon="regularArrowLeft"
              onClick={backToSettings}
              dataTestId="back-to-intake-settings"
            />
          )}
        </div>
      </div>
      {!inReviewView && (
        <>
          <Banner flagKey="builderBannerInfo" />
          <VoiceGuideBanner />
        </>
      )}
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.toolbarWrapper}>
            <div className={styles.toolbar}>
              {/* the Review forms highlight clears on the first use of any filter */}
              <div
                className={styles.toolbarFilters}
                onClickCapture={clearHighlight}
                onFocusCapture={clearHighlight}
              >
                <SearchInput
                  value={globalFilter?.state}
                  onChange={(e) =>
                    globalFilter?.onChange(e.currentTarget.value)
                  }
                  onClear={() => globalFilter?.onChange("")}
                  placeholder="Search"
                  dataTestId="search-forms-builder"
                  className="w-96"
                />
                {filters.map((f) => (
                  <Select {...f} />
                ))}
                <ContextMenu
                  button={{
                    className: "w-[2rem] h-[2rem]",
                    variant: "secondarySubtle",
                    leftIcon: "regularFilter",
                  }}
                  placement="bottom-end"
                  offsetBy={5}
                  items={filterOptions}
                  id="filter-forms"
                  className="w-fit mx-4"
                />
              </div>
              <div className={styles.toolbarActions}>
                {canActAsFirm && (
                  <ActAs loadTeam={loadTeam} loadedTeam={loadedTeam} />
                )}
                <Button
                  leftIcon="regularPlus"
                  variant="primaryDefault"
                  text="Create form"
                  onClick={() => setCreatingForm(true)}
                />
              </div>
            </div>
          </div>
          <Table
            data={data}
            columns={columns}
            updating={loading || loadingTeam}
            loadingRows={loading && !forms ? 3 : undefined}
            getRowId={(row) => row.uri}
            select={select}
            sort={sort}
            globalFilter={globalFilter}
            empty={
              <>
                <Icon
                  name="solidMagnifyingGlass"
                  className="bg-black/10 p-[1.3rem] rounded w-[2.2rem] h-[2.2rem]"
                />
                {getEmptyMessage()}
              </>
            }
            isRowClickable={(row) => !(row as GqlDeletedForm).deletedAt}
            onRowClick={onRowClick}
          />
          <CreatingFormModal
            creatingForm={creatingForm}
            forms={forms || []}
            setting={setting}
            onClose={() => setCreatingForm(undefined)}
          />
          <DeletingFormModal
            deletingForm={deletingForm}
            onClose={() => setDeletingForm(undefined)}
          />
          <DuplicatingFormModal
            duplicatingForm={duplicatingForm}
            forms={forms || []}
            onClose={() => setDuplicatingForm(undefined)}
          />
          <SetupModal loading={loading} setting={setting} />
          <Modal
            header={{
              icon: "solidCircleInfo",
              text: "Error loading firm",
            }}
            onClose={() => setErrorLoadingTeam(undefined)}
            open={!!errorLoadingTeam}
            footer={{
              buttons: [
                {
                  text: "OK",
                  type: "reset",
                  variant: "primaryDefault",
                  onClick: () => setErrorLoadingTeam(undefined),
                },
              ],
            }}
          >
            <p className="text-ui">{`This error occurred when trying to switch firms:\n${errorLoadingTeam}`}</p>
          </Modal>
        </div>
      </div>
      {printFormContent}
    </div>
  );
};
